import i18n from 'src/i18n';
import { SHOW_BILLING_RAG } from 'src/constants/featureFlags';
import {
  AddBusiness as AddBusinessIcon,
  AlternateEmail as AlternateEmailIcon,
  CheckCircle as CheckCircleIcon,
  Groups as GroupsIcon,
  Memory as MemoryIcon,
  WorkspacePremium as WorkspacePremiumIcon,
} from '@mui/icons-material';
import { Box, Card, Chip, Divider, Grid, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import NextPlanBillDetails from './next-plan-bill-detials.component';

const CurrentSubscriptionDetails = ({ currentPlanDetails, handleBuyPlan }) => {
  const { t: translate } = useTranslation();
  const theme = useTheme();
  const hasSubscription = currentPlanDetails.has_subscription;
  const hasTrailPeriod = currentPlanDetails.has_trial_period;
  const defaultPlanName = currentPlanDetails.default_plan_name;
  const isAppsumoUser = currentPlanDetails.is_appsumo_user;
  const expireDate = currentPlanDetails.expire_date;
  const paidPlanName = currentPlanDetails.paid_plan_name;
  const planDetails = currentPlanDetails.plan_details || {};
  const planAddonDetails = currentPlanDetails.plan_addon_detials || [];
  const isDefaultPlan = !hasSubscription && !hasTrailPeriod;
  const planStatusLabel = isDefaultPlan ? 'Default Plan' : hasTrailPeriod ? 'Trial Plan' : 'Active';
  const isAutoPayEnabled = Boolean(currentPlanDetails.is_auto_pay_enabled);
  const stripeSubscriptionStatus = currentPlanDetails.stripe_subscription_status || '';
  const stripeSubscriptionId = currentPlanDetails.stripe_subscription_id || '';
  const lastAutoPayError = currentPlanDetails.last_auto_pay_error || '';
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const autoPayStartDate =
    subscriptionDetails?.start_date || subscriptionDetails?.current_period_start || currentPlanDetails.auto_pay_start_date;
  const autoPayEndDate =
    subscriptionDetails?.cancel_at ||
    subscriptionDetails?.current_period_end ||
    currentPlanDetails.auto_pay_end_date ||
    currentPlanDetails.expire_date;
  const nextPaymentDate = subscriptionDetails?.current_period_end || currentPlanDetails.auto_pay_end_date;
  const subscriptionStatus = subscriptionDetails?.status || stripeSubscriptionStatus;
  const subscriptionError = subscriptionDetails?.last_payment_error || lastAutoPayError;

  useEffect(() => {
    if (!isAutoPayEnabled || !stripeSubscriptionId) {
      setSubscriptionDetails(null);
      return;
    }
    HttpServices.Get({
      url: userModuleURL.getStripeSubscriptionDetails,
      successCallback: (data) => setSubscriptionDetails(data || null),
      failureCallback: () => setSubscriptionDetails(null),
    });
  }, [isAutoPayEnabled, stripeSubscriptionId]);

  const getAddonQuantity = (name) => planAddonDetails.find((e) => e.name === name)?.quantity || 0;

  const getLimitDetails = (details = {}, keyPrefix) => {
    const addonQuantity = getAddonQuantity(keyPrefix);
    const addonLimit = Number(details[`${keyPrefix}_addon`] || 0);
    const total = Number(details[`${keyPrefix}_total`] || 0) + Number(addonQuantity) * addonLimit - addonLimit;
    const currentUsed = Number(details[`${keyPrefix}_current_used`] || 0);
    const planLimit = Number(details[`${keyPrefix}_plan`] || 0);
    const upgradedLimit = addonLimit * Number(addonQuantity);

    return {
      total: Math.max(total, 0),
      currentUsed,
      planLimit,
      upgradedLimit,
      remaining: Math.max(total - currentUsed, 0),
    };
  };

  const userDetails = getLimitDetails(planDetails.no_of_users, 'no_of_users');
  const brandDetails = getLimitDetails(planDetails.no_of_brands, 'no_of_brands');
  const cName = planDetails.cname || {};
  const ragDetails = planDetails.rag || planDetails.rag_storage || {};
  const hasRag =
    Boolean(currentPlanDetails.has_rag) ||
    Boolean(planDetails.has_rag) ||
    Boolean(ragDetails.has_rag);

  const getPlanName = () => {
    if (isAppsumoUser) {
      switch (defaultPlanName) {
        case 'brand_lifetime':
          return translate('billing.lifetime_plan_info.brand_lifetime');
        case 'agency_lifetime':
        case 'mini_agency_lifetime':
          return translate('billing.lifetime_plan_info.agency_lifetime');
        case 'business_lifetime':
          return translate('billing.lifetime_plan_info.business_lifetime');
        case 'team_lifetime':
          return translate('billing.lifetime_plan_info.team_lifetime');
        default:
          return defaultPlanName ? defaultPlanName.replaceAll('_', ' ') : translate('billing.lifetime_plan_info.lifetime_plan');
      }
    }

    return paidPlanName || defaultPlanName?.replaceAll('_', ' ') || translate('billing.subscription_info.plan');
  };

  const DetailCard = ({ icon, title, children, accentColor = theme.palette.primary.main }) => (
    <Card
      sx={{
        p: 2.5,
        height: '100%',
        width: '100%',
        borderRadius: 1,
        boxShadow: 'none',
        border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1,
              display: 'grid',
              placeItems: 'center',
              color: accentColor,
              bgcolor: alpha(accentColor, 0.1),
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Typography sx={{ fontWeight: 800 }}>{title}</Typography>
        </Stack>
        <Stack spacing={1.25}>{children}</Stack>
      </Stack>
    </Card>
  );

  const DetailRow = ({ label, value, valueColor = 'text.primary' }) => (
    <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="center">
      <Typography color="text.secondary" sx={{ fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 800, color: valueColor, textAlign: 'right', textTransform: 'capitalize' }}>
        {value}
      </Typography>
    </Stack>
  );

  const getPlanView = () => (
    <DetailCard
      icon={<WorkspacePremiumIcon fontSize="small" />}
      title={isAppsumoUser ? translate('billing.lifetime_plan_info.lifetime_plan_details') : translate('billing.subscription_info.title')}
    >
      <DetailRow label={translate('billing.subscription_info.pricing_plan')} value={getPlanName()} />
      {!isDefaultPlan && (
        <DetailRow
          label={translate('billing.subscription_info.expires_on')}
          value={expireDate ? CommonHelper.formatDateAndTime(expireDate, 'date') : '-'}
        />
      )}
      <DetailRow label={translate('billing.lifetime_plan_info.status')} value={planStatusLabel} />
    </DetailCard>
  );

  const getBrandView = () => (
    <DetailCard icon={<AddBusinessIcon fontSize="small" />} title={translate('billing.lifetime_plan_info.brand_details')} accentColor={theme.palette.info.main}>
      <DetailRow label={translate('billing.lifetime_plan_info.total_brands')} value={brandDetails.total} />
      <DetailRow label={translate('billing.lifetime_plan_info.brands_per_plan')} value={brandDetails.planLimit} />
      <DetailRow label={translate('billing.lifetime_plan_info.upgraded_brands')} value={brandDetails.upgradedLimit} />
      <DetailRow label={translate('billing.lifetime_plan_info.remaining_brands')} value={brandDetails.remaining} />
    </DetailCard>
  );

  const getUsersView = () => (
    <DetailCard icon={<GroupsIcon fontSize="small" />} title={translate('billing.subscription_info.users')}>
      <DetailRow label={translate('billing.subscription_info.total_users')} value={userDetails.total} />
      <DetailRow label={translate('billing.subscription_info.total_users_per_plan')} value={userDetails.planLimit} />
      <DetailRow label={translate('billing.subscription_info.upgraded_users_per_plan')} value={userDetails.upgradedLimit} />
      <DetailRow label={translate('billing.subscription_info.remaining_users')} value={userDetails.remaining} />
    </DetailCard>
  );

  const getRagView = () => {
    return (
      <DetailCard icon={<MemoryIcon fontSize="small" />} title={translate('billing.lifetime_plan_info.rag_details')} accentColor={theme.palette.warning.main}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <Typography color="text.secondary" sx={{ fontWeight: 700 }}>
            {translate('billing.lifetime_plan_info.rag_enabled')}
          </Typography>
          <Chip
            size="small"
            color={hasRag ? 'success' : 'default'}
            label={hasRag ? translate('common.yes') : translate('common.no')}
          />
        </Stack>
        <DetailRow
          label={translate('billing.lifetime_plan_info.benefit')}
          value={hasRag ? translate('billing.lifetime_plan_info.ai_cost_reduction') : '-'}
        />
      </DetailCard>
    );
  };

  const getCustomDomainView = () => {
    const hasCustomDomain =
      Boolean(currentPlanDetails.has_cname) || Boolean(planDetails.has_cname) || Boolean(cName.has_cname);

    return (
      <DetailCard
        icon={<AlternateEmailIcon fontSize="small" />}
        title={translate('billing.lifetime_plan_info.custom_domain')}
        accentColor={theme.palette.success.main}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <Typography color="text.secondary" sx={{ fontWeight: 700 }}>
            {translate('billing.lifetime_plan_info.custom_domain_available')}
          </Typography>
          <Chip
            size="small"
            color={hasCustomDomain ? 'success' : 'default'}
            label={hasCustomDomain ? translate('common.yes') : translate('common.no')}
          />
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ color: hasCustomDomain ? 'success.main' : 'text.disabled' }}
        >
          <CheckCircleIcon fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {hasCustomDomain ? translate('billing.lifetime_plan_info.ready_to_use_branded_domain') : translate('billing.lifetime_plan_info.custom_domain_not_enabled')}
          </Typography>
        </Stack>
      </DetailCard>
    );
  };

  const getAutoPayView = () => {
    if (!hasSubscription || hasTrailPeriod || isAppsumoUser || !isAutoPayEnabled) {
      return null;
    }

    return (
      <DetailCard
        icon={<CheckCircleIcon fontSize="small" />}
        title={translate('billing.subscription_info.autopay_enabled', { defaultValue: 'Autopay enabled' })}
        accentColor={subscriptionStatus === 'past_due' ? theme.palette.warning.main : theme.palette.success.main}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <Typography color="text.secondary" sx={{ fontWeight: 700 }}>
            Status
          </Typography>
          <Chip
            size="small"
            color={subscriptionStatus === 'past_due' ? 'warning' : 'success'}
            label={subscriptionStatus || 'active'}
          />
        </Stack>
        <DetailRow
          label={translate('billing.subscription_info.subscription_start', { defaultValue: 'Subscription start' })}
          value={autoPayStartDate ? CommonHelper.formatDateAndTime(autoPayStartDate, 'date') : '-'}
        />
        <DetailRow
          label={translate('billing.subscription_info.subscription_end', { defaultValue: 'Subscription end' })}
          value={autoPayEndDate ? CommonHelper.formatDateAndTime(autoPayEndDate, 'date') : '-'}
        />
        <DetailRow
          label={translate('billing.subscription_info.next_payment', { defaultValue: 'Next payment' })}
          value={
            nextPaymentDate
              ? CommonHelper.formatDateAndTime(nextPaymentDate, 'date')
              : translate('billing.subscription_info.charged_monthly_by_stripe', {
                  defaultValue: 'Charged monthly by Stripe',
                })
          }
        />
        {stripeSubscriptionId && (
          <Typography variant="caption" color="text.secondary">
            {translate('billing.subscription_info.stripe_subscription_id', {
              defaultValue: 'Stripe subscription ID',
            })}
            : {stripeSubscriptionId}
          </Typography>
        )}
        {subscriptionError && (
          <Typography variant="caption" color="error">
            {subscriptionError}
          </Typography>
        )}
      </DetailCard>
    );
  };

  const autoPayView = getAutoPayView();

  return (
    <Card
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 1,
        boxShadow: 'none',
        border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
      }}
    >
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {translate('billing.subscription_info.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('billing.lifetime_plan_info.description')}
          </Typography>
        </Box>
        <Divider />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} xl={4} sx={{ display: 'flex' }}>
            {getPlanView()}
          </Grid>
          <Grid item xs={12} md={6} xl={4} sx={{ display: 'flex' }}>
            {getBrandView()}
          </Grid>
          <Grid item xs={12} md={6} xl={4} sx={{ display: 'flex' }}>
            {getUsersView()}
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <DetailCard icon={<GroupsIcon />} title={translate('billing.social_accounts.title', { defaultValue: 'Social media accounts' })}>
              <Typography variant="h5">
                {planDetails.no_of_social_accounts?.no_of_social_accounts_current_used || 0} / {planDetails.no_of_social_accounts?.no_of_social_accounts_total || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {['professional', 'pro'].includes(String(currentPlanDetails.plan_type || '').trim().toLowerCase())
                  ? i18n.t('plan_access.channels')
                  : i18n.t('plan_access.channels_basic')}
              </Typography>
            </DetailCard>
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <DetailCard icon={<MemoryIcon fontSize="small" />} title="AI Chat">
              <Typography variant="body2">
                {CommonHelper.HasAIChatPlan(currentPlanDetails) ? i18n.t('plan_access.ai_included') : i18n.t('plan_access.ai_excluded')}
              </Typography>
            </DetailCard>
          </Grid>
          {SHOW_BILLING_RAG && <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            {getRagView()}
          </Grid>}
          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            {getCustomDomainView()}
          </Grid>
          {autoPayView && (
            <Grid item xs={12} sx={{ display: 'flex' }}>
              {autoPayView}
            </Grid>
          )}
        </Grid>
        <NextPlanBillDetails
          hasSubscription={hasSubscription}
          hasTrailPeriod={hasTrailPeriod}
          expireDate={expireDate}
          isAppsumoUser={isAppsumoUser}
          defaultPlanName={defaultPlanName}
          paidPlanName={paidPlanName}
          handleBuyPlan={handleBuyPlan}
          isAutoPayEnabled={isAutoPayEnabled}
          stripeSubscriptionStatus={stripeSubscriptionStatus}
        />
      </Stack>
    </Card>
  );
};

export default CurrentSubscriptionDetails;
